import { observer } from 'mobx-react-lite'
import { useCallback } from 'react'
import { useTranslationTyped } from 'i18n/i18n'
import type {
  StudentPeerReviewController,
  RevieweeToReview,
} from '@breakoutlearning/firebase-repository/cubits/StudentAssignment/StudentPeerReviewController'
import { PeerReviewGrid } from 'components/PeerReview/PeerReviewGrid'
import { PeerReviewMeaningfulScore } from '@breakoutlearning/firebase-repository/models/RoomStateUPRPeerReview'
import type { SectionAssignmentPeerReviewQuestion } from '@breakoutlearning/firebase-repository/models/SectionAssignmentPeerReviewQuestion'
import { BreakoutAsyncButton } from 'components/design-system/BreakoutButton'
import { ChevronRight } from 'components/icons/ChevronRight'
import classNames from 'classnames'

export const PeerReviewFeedbackForm = observer(function PeerReviewFeedbackForm({
  className,
  peerReviewQuestions,
  reviewsOfUserByReviewee,
  controller,
  onSaveComplete,
  stagedReviews,
  setStagedReviews,
  stagedNotes,
  setStagedNotes,
  stagedSharing,
  setStagedSharing,
  'aria-hidden': ariaHidden,
}: {
  className?: string
  peerReviewQuestions: SectionAssignmentPeerReviewQuestion[]
  reviewsOfUserByReviewee: RevieweeToReview
  controller: StudentPeerReviewController
  onSaveComplete: () => void
  stagedReviews: Map<string, Map<string, PeerReviewMeaningfulScore>>
  setStagedReviews: React.Dispatch<
    React.SetStateAction<Map<string, Map<string, PeerReviewMeaningfulScore>>>
  >
  stagedNotes: Map<string, string>
  setStagedNotes: React.Dispatch<React.SetStateAction<Map<string, string>>>
  stagedSharing: Map<string, boolean>
  setStagedSharing: React.Dispatch<React.SetStateAction<Map<string, boolean>>>
  'aria-hidden'?: boolean
}) {
  const { tt } = useTranslationTyped()

  const handleNoteChange = useCallback(
    (revieweeUserId: string, note: string) => {
      setStagedNotes((prev) => {
        const newMap = new Map(prev)
        if (note) {
          newMap.set(revieweeUserId, note)
        } else {
          // If note is empty, mark it for deletion by setting to empty string
          // This distinguishes between "no change" and "removed"
          const existingNote = reviewsOfUserByReviewee.get(revieweeUserId)?.note
          if (existingNote) {
            newMap.set(revieweeUserId, '')
          } else {
            newMap.delete(revieweeUserId)
          }
        }
        return newMap
      })
    },
    [setStagedNotes, reviewsOfUserByReviewee]
  )

  const handleSharingChange = useCallback(
    (revieweeUserId: string, shared: boolean) => {
      setStagedSharing((prev) => {
        const newMap = new Map(prev)
        newMap.set(revieweeUserId, shared)
        return newMap
      })
    },
    [setStagedSharing]
  )

  const handleScoreChange = useCallback(
    (
      revieweeUserId: string,
      questionId: string,
      scoreOption: PeerReviewMeaningfulScore
    ) => {
      setStagedReviews((prev) => {
        const newMap = new Map(prev)
        const revieweeReviews = newMap.get(revieweeUserId) || new Map()
        const newRevieweeReviews = new Map(revieweeReviews)

        // If NA is selected, set all questions for this reviewee to NA
        if (scoreOption === PeerReviewMeaningfulScore.NA) {
          for (const question of peerReviewQuestions) {
            newRevieweeReviews.set(question.id, PeerReviewMeaningfulScore.NA)
          }
        } else {
          newRevieweeReviews.set(questionId, scoreOption)
        }

        newMap.set(revieweeUserId, newRevieweeReviews)
        return newMap
      })
    },
    [setStagedReviews, peerReviewQuestions]
  )

  const revieweesState = controller.reviewees
  const reviewees =
    revieweesState.isLoaded && revieweesState.value ? revieweesState.value : []

  const hasMissingReviewsState = controller.hasMissingReviews(stagedReviews)
  const hasMissingReviews =
    !hasMissingReviewsState.isLoaded || hasMissingReviewsState.value

  const handleSave = useCallback(async () => {
    await controller.saveStagedPeerReviewsAndNotes({
      stagedReviews,
      stagedNotes,
      stagedSharing,
      reviewsOfUserByReviewee,
    })
    setStagedReviews(new Map())
    setStagedNotes(new Map())
    setStagedSharing(new Map())
    onSaveComplete()
  }, [
    controller,
    stagedReviews,
    stagedNotes,
    stagedSharing,
    reviewsOfUserByReviewee,
    setStagedReviews,
    setStagedNotes,
    setStagedSharing,
    onSaveComplete,
  ])

  return (
    <div
      className={classNames('flex flex-1 flex-col overflow-auto', className)}
      aria-hidden={ariaHidden}
    >
      <PeerReviewGrid
        peerReviewQuestions={peerReviewQuestions}
        reviewees={reviewees}
        readOnly={false}
        stagedReviews={stagedReviews}
        onScoreChange={handleScoreChange}
        stagedNotes={stagedNotes}
        onNoteChange={handleNoteChange}
        stagedSharing={stagedSharing}
        onSharingChange={handleSharingChange}
      />
      <div className="flex flex-1 flex-col items-end justify-end border-t border-outline-light bg-surface-bright pt-4">
        <BreakoutAsyncButton
          kind="accent"
          iconOnRight
          icon={<ChevronRight size={16} />}
          onClick={handleSave}
          disabled={hasMissingReviews}
        >
          {tt.student_assignment.peer_review_dialog.submit_peer_review()}
        </BreakoutAsyncButton>
      </div>
    </div>
  )
})
